import { localized } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import type { PromiseWithServerResult } from "@rocicorp/zero";

/**
 * One toast id for the whole sync path, so a refusal repeated by a drag that keeps snapping back
 * replaces the notice instead of stacking another copy of it.
 */
const SYNC_TOAST_ID = "sync-mutation";

/**
 * Read the server's verdict on a board mutation and tell the person when it was a refusal.
 *
 * `responseHandler` does this for the REST path, and the sync path needs its own because the
 * shape is different: `z.mutate(...)` applies the change locally at once and returns two promises,
 * one for that optimistic run and one for what the server made of it. Neither rejects — the
 * refusal arrives as a resolved `{ type: "error" }` — so a call site that ignores the result gets
 * the card silently snapping back on the next rebase, which is indistinguishable from a bug in the
 * board.
 *
 * The two kinds of error say different things and are not merged. An `app` error is the mutator
 * throwing: the gate refused the rule, or a guard refused an id. A `zero` error is the sync
 * connection — offline, closed, protocol — where the mutation is rolled back for a reason that has
 * nothing to do with permission, and saying "not allowed" there would be a lie.
 *
 * @param mutation - What `z.mutate(...)` returned.
 * @param withToast - Pass false where the caller shows the refusal itself.
 * @returns Whether the server accepted the mutation.
 */
export async function syncHandler(mutation: PromiseWithServerResult, withToast = true) {
	const verdict = await mutation.server;
	if (verdict.type !== "error") return true;
	if (withToast) {
		const message =
			verdict.error.type === "app"
				? localized("planning.change_not_allowed")
				: localized("general.something_went_wrong");
		toast.error(message, { id: SYNC_TOAST_ID });
	}
	return false;
}
